import {
  CENTRAL_VALIDATION_LIMITS,
  INSTALLMENT_VALIDATION_LIMITS,
  UC_VALIDATION_LIMITS,
} from "../constants/centralConstants";
import { financialYearUtils } from "./helpers";

// Validate required field
export const validateRequired = (value, fieldName = "Field") => {
  if (!value || (typeof value === "string" && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate project name
export const validateProjectName = (projectName) => {
  if (!projectName || !projectName.trim()) {
    return "Project name is required";
  }

  const trimmed = projectName.trim();

  if (trimmed.length < CENTRAL_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH) {
    return `Project name must be at least ${CENTRAL_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`;
  }

  if (trimmed.length > CENTRAL_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH) {
    return `Project name cannot exceed ${CENTRAL_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`;
  }

  return null;
};

// UPDATED: Validate amount - allow 0 for state share
export const validateAmount = (
  amount,
  fieldName = "Amount",
  allowZero = false,
) => {
  if (!amount || amount.toString().trim() === "") {
    return `${fieldName} is required`;
  }

  const numAmount = parseFloat(amount);

  if (isNaN(numAmount)) {
    return `Please enter a valid ${fieldName.toLowerCase()}`;
  }

  // Allow zero for state share when explicitly permitted
  const minValue = allowZero ? 0 : CENTRAL_VALIDATION_LIMITS.AMOUNT_MIN;

  if (numAmount < minValue) {
    return allowZero
      ? `${fieldName} cannot be negative`
      : `${fieldName} must be greater than 0`;
  }

  if (numAmount > CENTRAL_VALIDATION_LIMITS.AMOUNT_MAX) {
    return `${fieldName} cannot exceed ₹99,999 lakhs`;
  }

  return null;
};

// Validate installment number
export const validateInstallmentNumber = (number) => {
  if (!number || number.toString().trim() === "") {
    return "Installment number is required";
  }

  const numInstallment = parseInt(number);

  if (isNaN(numInstallment)) {
    return "Please enter a valid installment number";
  }

  if (numInstallment < INSTALLMENT_VALIDATION_LIMITS.INSTALLMENT_NUMBER_MIN) {
    return "Installment number must be at least 1";
  }

  if (numInstallment > INSTALLMENT_VALIDATION_LIMITS.INSTALLMENT_NUMBER_MAX) {
    return "Installment number cannot exceed 50";
  }

  return null;
};

// UPDATED: Validate physical progress - allow decimal values
export const validatePhysicalProgress = (progress) => {
  if (!progress || progress.toString().trim() === "") {
    return "Physical progress is required";
  }

  const numProgress = parseFloat(progress);

  if (isNaN(numProgress)) {
    return "Please enter a valid progress percentage";
  }

  if (numProgress < INSTALLMENT_VALIDATION_LIMITS.PHYSICAL_PROGRESS_MIN) {
    return "Physical progress cannot be negative";
  }

  if (numProgress > INSTALLMENT_VALIDATION_LIMITS.PHYSICAL_PROGRESS_MAX) {
    return "Physical progress cannot exceed 100%";
  }

  return null;
};

// Validate Date of Sanction (for projects)
export const validateWorkOrderNumber = (workOrderNumber) => {
  if (!workOrderNumber || !workOrderNumber.trim()) {
    return "Date of Sanction is required";
  }

  const trimmed = workOrderNumber.trim();

  if (trimmed.length < CENTRAL_VALIDATION_LIMITS.WORK_ORDER_NUMBER_MIN_LENGTH) {
    return "Date of Sanction is required";
  }

  if (trimmed.length > CENTRAL_VALIDATION_LIMITS.WORK_ORDER_NUMBER_MAX_LENGTH) {
    return `Date of Sanction cannot exceed ${CENTRAL_VALIDATION_LIMITS.WORK_ORDER_NUMBER_MAX_LENGTH} characters`;
  }

  return null;
};

// REMOVED: validateReleasedBy function since we're removing the field

// Validate UC number
export const validateUCNumber = (ucNumber) => {
  if (!ucNumber || !ucNumber.trim()) {
    return "UC number is required";
  }

  const trimmed = ucNumber.trim();

  if (trimmed.length < UC_VALIDATION_LIMITS.UC_NUMBER_MIN_LENGTH) {
    return `UC number must be at least ${UC_VALIDATION_LIMITS.UC_NUMBER_MIN_LENGTH} characters`;
  }

  if (trimmed.length > UC_VALIDATION_LIMITS.UC_NUMBER_MAX_LENGTH) {
    return `UC number cannot exceed ${UC_VALIDATION_LIMITS.UC_NUMBER_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate date
export const validateDate = (date, fieldName = "Date") => {
  if (!date) {
    return `${fieldName} is required`;
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return `Please enter a valid ${fieldName.toLowerCase()}`;
  }

  return null;
};

// UPDATED: Validate central project form - allow state share to be 0
export const validateCentralProjectForm = (formData) => {
  const errors = {};

  // Project name validation
  const nameError = validateProjectName(formData.projectName);
  if (nameError) errors.projectName = nameError;

  // Scheme validation
  const schemeError = validateRequired(formData.scheme, "Scheme");
  if (schemeError) errors.scheme = schemeError;

  // Central share validation (DoNER/NEC Share)
  const centralShareError = validateAmount(
    formData.centralShare,
    "Central share",
  );
  if (centralShareError) errors.centralShare = centralShareError;

  // UPDATED: State share validation - allow 0
  const stateShareError = validateAmount(
    formData.stateShare,
    "State share",
    true,
  );
  if (stateShareError) errors.stateShare = stateShareError;

  // Date of Sanction validation
  const workOrderNumberError = validateWorkOrderNumber(
    formData.workOrderNumber,
  );
  if (workOrderNumberError) errors.workOrderNumber = workOrderNumberError;

  // Work order issue date validation
  const workOrderDateError = validateDate(
    formData.workOrderIssueDate,
    "Work order issue date",
  );
  if (workOrderDateError) errors.workOrderIssueDate = workOrderDateError;

  // Scheduled completion date validation
  const completionDateError = validateDate(
    formData.scheduledCompletionDate,
    "Scheduled completion date",
  );
  if (completionDateError) errors.scheduledCompletionDate = completionDateError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// UPDATED: Validate installment form - removed amountReleasedBy validation
export const validateInstallmentForm = (formData) => {
  const errors = {};

  // Financial year validation
  const financialYearError = validateRequired(
    formData.financialYear,
    "Financial year",
  );
  if (financialYearError) errors.financialYear = financialYearError;

  // Project validation
  const projectError = validateRequired(formData.projectId, "Project");
  if (projectError) errors.projectId = projectError;

  // REMOVED: Released by validation

  // Installment number validation
  const installmentError = validateInstallmentNumber(
    formData.installmentNumber,
  );
  if (installmentError) errors.installmentNumber = installmentError;

  // Release date validation
  const releaseDateError = validateDate(formData.releaseDate, "Release date");
  if (releaseDateError) errors.releaseDate = releaseDateError;

  // Amount validation
  const amountError = validateAmount(formData.amount, "Amount");
  if (amountError) errors.amount = amountError;

  // Physical progress validation (now allows decimals)
  const progressError = validatePhysicalProgress(formData.physicalProgress);
  if (progressError) errors.physicalProgress = progressError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validate UC form
export const validateUCForm = (formData) => {
  const errors = {};

  // Financial year validation
  const financialYearError = validateRequired(
    formData.financialYear,
    "Financial year",
  );
  if (financialYearError) errors.financialYear = financialYearError;

  // Project validation
  const projectError = validateRequired(formData.projectId, "Project");
  if (projectError) errors.projectId = projectError;

  // UC number validation
  const ucNumberError = validateUCNumber(formData.ucNumber);
  if (ucNumberError) errors.ucNumber = ucNumberError;

  // UC certificate date validation
  const ucCertificateDateError = validateDate(
    formData.ucCertificateDate,
    "UC certificate date",
  );
  if (ucCertificateDateError) errors.ucCertificateDate = ucCertificateDateError;

  // UC furnished amount validation
  const ucFurnishedError = validateAmount(
    formData.ucFurnished,
    "UC furnished amount",
  );
  if (ucFurnishedError) errors.ucFurnished = ucFurnishedError;

  // UC due amount validation
  const ucDueError = validateAmount(formData.ucDue, "UC due amount");
  if (ucDueError) errors.ucDue = ucDueError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Sanitize central project data - with work order fields
export const sanitizeCentralProjectData = (projectData) => {
  const sanitized = { ...projectData };

  // Trim string fields
  if (sanitized.projectName)
    sanitized.projectName = sanitized.projectName.trim();
  if (sanitized.scheme) sanitized.scheme = sanitized.scheme.trim();
  if (sanitized.workOrderNumber)
    sanitized.workOrderNumber = sanitized.workOrderNumber.trim();

  // Auto-calculate financial year
  sanitized.financialYear = financialYearUtils.getCurrentFinancialYear();

  // Parse amounts
  if (sanitized.centralShare) {
    sanitized.centralShare = parseFloat(
      sanitized.centralShare.toString().replace(/[^\d.]/g, ""),
    );
  }

  if (sanitized.stateShare) {
    sanitized.stateShare = parseFloat(
      sanitized.stateShare.toString().replace(/[^\d.]/g, ""),
    );
  }

  // Calculate total
  const centralAmount = sanitized.centralShare || 0;
  const stateAmount = sanitized.stateShare || 0;
  sanitized.totalAmount = centralAmount + stateAmount;

  // Handle work order dates
  if (sanitized.workOrderIssueDate) {
    sanitized.workOrderIssueDate = new Date(sanitized.workOrderIssueDate);
  }

  if (sanitized.scheduledCompletionDate) {
    sanitized.scheduledCompletionDate = new Date(
      sanitized.scheduledCompletionDate,
    );
  }

  // Remove undefined values
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// UPDATED: Sanitize installment data - removed amountReleasedBy field
export const sanitizeInstallmentData = (installmentData) => {
  const sanitized = { ...installmentData };

  // REMOVED: amountReleasedBy trimming since field is removed

  // Parse numeric fields
  if (sanitized.installmentNumber) {
    sanitized.installmentNumber = parseInt(sanitized.installmentNumber);
  }

  if (sanitized.amount) {
    sanitized.amount = parseFloat(
      sanitized.amount.toString().replace(/[^\d.]/g, ""),
    );
  }

  // UPDATED: Allow decimal values for physical progress
  if (sanitized.physicalProgress) {
    sanitized.physicalProgress = parseFloat(
      sanitized.physicalProgress.toString().replace(/[^\d.]/g, ""),
    );
  }

  // Handle dates
  if (sanitized.releaseDate) {
    sanitized.releaseDate = new Date(sanitized.releaseDate);
  }

  // Remove undefined values
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// Sanitize UC data
export const sanitizeUCData = (ucData) => {
  const sanitized = { ...ucData };

  // Trim string fields
  if (sanitized.ucNumber) sanitized.ucNumber = sanitized.ucNumber.trim();

  // Handle UC certificate date
  if (sanitized.ucCertificateDate) {
    sanitized.ucCertificateDate = new Date(sanitized.ucCertificateDate);
  }

  // Parse amount fields
  if (sanitized.ucFurnished) {
    sanitized.ucFurnished = parseFloat(
      sanitized.ucFurnished.toString().replace(/[^\d.]/g, ""),
    );
  }

  if (sanitized.ucDue) {
    sanitized.ucDue = parseFloat(
      sanitized.ucDue.toString().replace(/[^\d.]/g, ""),
    );
  }

  // Ensure centralShare is a number
  if (sanitized.centralShare) {
    sanitized.centralShare = parseFloat(sanitized.centralShare);
  }

  // Remove undefined values
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};
